import moment from "moment";
import { showGenericTwoButtonDialogAction } from "redux/actions/dialogsAction";
import { DIALOG } from "utils/actionsConsts";
import {
  COOPERATIVE_MEMBER_AGE_DISCLAIMER,
  COOPERATIVE_MEMBER_MAX_AGE,
  COOPERATIVE_MEMBER_MIN_AGE,
  REGEX_PATTERNS,
  normalizeAOIBAN,
} from "utils/const";

export const COOPERATIVE_MEMBERS_DEFAULT_FORM_VALUES = {
  name: "",
  nif: "",
  documentNumber: "",
  functionId: "",
  startDate: null,
  endDate: null,
  showInactive: false,
  expiringIn30Days: false,
  pageIndex: 1,
  pageSize: 15,
};

export const EMPTY_INACTIVATION_FORM_VALUES = {
  inactivationReasonInput: "",
  inactivationDocument: [],
};

export const REQUIRED_FIELD_RULE = {
  required: {
    value: true,
    message: "Campo obrigatório.",
  },
};

const formatOptionalDate = (date) => (date ? moment.utc(date).format() : null);

const appendOptionalField = (formData, fieldName, value) => {
  if (value !== undefined && value !== null && value !== "") {
    formData.append(fieldName, value);
  }
};

const appendOptionalFile = (formData, fieldName, file) => {
  if (file) {
    formData.append(fieldName, file, file.name);
  }
};

const appendExistingDocumentReference = (formData, apiPrefix, documentKey, documentName) => {
  if (!documentKey) {
    return;
  }

  formData.append(`CooperativeMember.${apiPrefix}Key`, documentKey);
  formData.append(`${apiPrefix}Key`, documentKey);

  if (documentName) {
    formData.append(`CooperativeMember.${apiPrefix}Name`, documentName);
    formData.append(`${apiPrefix}Name`, documentName);
  }
};

const resolveDocument = (
  availableDocuments,
  documentName,
  fallbackDocumentKey,
  fallbackDocumentTypeId,
  fallbackMatcher,
) => {
  const matchingDocument =
    availableDocuments.find((document) => document?.documentTypeId === fallbackDocumentTypeId) ??
    availableDocuments.find((document) => (document?.documentName ?? document?.name) === documentName) ??
    (fallbackMatcher ? availableDocuments.find(fallbackMatcher) : null);

  if (!documentName && !fallbackDocumentKey && !matchingDocument) {
    return null;
  }

  return {
    documentKey: fallbackDocumentKey ?? matchingDocument?.documentKey ?? matchingDocument?.key,
    documentName: documentName ?? matchingDocument?.documentName ?? matchingDocument?.name,
  };
};

export const buildCooperativeMembersQueryParams = (
  values,
  foUserCollectiveId,
  sortColumn,
  sortDirection,
  overrides,
) => ({
  name: values.name ?? "",
  nif: values.nif ?? "",
  documentNumber: values.documentNumber ?? "",
  functionId: values.functionId || undefined,
  startDate: values.startDate ? moment(values.startDate).format() : undefined,
  endDate: values.endDate ? moment(values.endDate).format() : undefined,
  showInactive: values.showInactive || undefined,
  expiringIn30Days: values.expiringIn30Days || undefined,
  foUserCollectiveId,
  pageIndex: overrides?.pageIndex ?? values.pageIndex ?? 1,
  pageSize: values.pageSize ?? 15,
  sortColumn: sortColumn || undefined,
  sortDirection: sortDirection || undefined,
});

export const getApiErrorMessage = (error, fallback) => {
  if (error?.response?.data?.errors) {
    return Object.values(error.response.data.errors).flat().join(". ");
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  return fallback;
};

export const getBirthDateRules = () => ({
  ...REQUIRED_FIELD_RULE,
  validate: {
    ageRange: (value) => {
      if (!value) {
        return true;
      }

      const age = moment().diff(moment(value), "years");

      if (age < COOPERATIVE_MEMBER_MIN_AGE || age > COOPERATIVE_MEMBER_MAX_AGE) {
        return COOPERATIVE_MEMBER_AGE_DISCLAIMER;
      }

      return true;
    },
  },
});

export const getIbanRules = () => ({
  validate: {
    validAOIBAN: (value) => {
      if (!value) {
        return true;
      }

      return (
        REGEX_PATTERNS.AO_IBAN.test(value) ||
        "O IBAN deve conter 25 caracteres, começando por AO06 e seguido de 21 dígitos."
      );
    },
  },
});

export const getIdentityDocumentExpiryRules = () => ({
  validate: {
    notPast: (value) => {
      if (!value) {
        return true;
      }

      if (moment(value).isBefore(moment(), "day")) {
        return "Não é possível submeter datas anteriores à actual.";
      }

      return true;
    },
  },
});

export const buildCreateCooperativeMemberFormData = ({ cooperativeId, values }) => {
  const formData = new FormData();

  appendOptionalField(formData, "CooperativeMember.Name", values.name);
  appendOptionalField(formData, "CooperativeMember.CooperativeId", cooperativeId);
  appendOptionalField(formData, "CooperativeMember.FunctionId", values.functionId);
  appendOptionalField(formData, "CooperativeMember.DateOfBirth", formatOptionalDate(values.dateOfBirth));
  appendOptionalField(formData, "CooperativeMember.NIF", values.nif);
  appendOptionalField(
    formData,
    "CooperativeMember.BankAccountNumber",
    values.bankAccountNumber ? normalizeAOIBAN(values.bankAccountNumber) : null,
  );
  appendOptionalField(formData, "CooperativeMember.DocumentTypeId", values.documentTypeId);
  appendOptionalField(formData, "CooperativeMember.IdentityDocumentNumber", values.identityDocumentNumber);
  appendOptionalField(
    formData,
    "CooperativeMember.IdentityDocumentIssueDate",
    formatOptionalDate(values.identityDocumentIssueDate),
  );
  appendOptionalField(
    formData,
    "CooperativeMember.IdentityDocumentExpiryDate",
    formatOptionalDate(values.identityDocumentExpiryDate),
  );
  appendOptionalFile(formData, "IdentityDocument", values.identityDocument?.[0]);
  appendOptionalFile(
    formData,
    "EntrepreneurshipCertificateDocument",
    values.entrepreneurshipCertificate?.[0],
  );

  return formData;
};

export const COOPERATIVE_MEMBER_API_ERROR_FIELD_MAP = {
  name: "name",
  functionid: "functionId",
  dateofbirth: "dateOfBirth",
  nif: "nif",
  bankaccountnumber: "bankAccountNumber",
  documenttypeid: "documentTypeId",
  identitydocumentnumber: "identityDocumentNumber",
  identitydocumentissuedate: "identityDocumentIssueDate",
  identitydocumentexpirydate: "identityDocumentExpiryDate",
  identitydocument: "identityDocument",
  identitydocumentkey: "identityDocument",
  identitydocumentname: "identityDocument",
  entrepreneurshipcertificatedocument: "entrepreneurshipCertificate",
  entrepreneurshipcertificatedocumentkey: "entrepreneurshipCertificate",
  entrepreneurshipcertificatedocumentname: "entrepreneurshipCertificate",
};

export const normalizeApiErrorFieldKey = (fieldKey = "") =>
  fieldKey
    .split(".")
    .pop()
    .replace(/\[\d+\]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();

export const applyCooperativeMemberApiErrors = (errors, setError) => {
  if (!errors) {
    return [];
  }

  return Object.entries(errors).reduce((unmappedMessages, [fieldKey, messages]) => {
    const fieldName = COOPERATIVE_MEMBER_API_ERROR_FIELD_MAP[normalizeApiErrorFieldKey(fieldKey)];
    const message = Array.isArray(messages) ? messages[0] : messages;

    if (fieldName && message) {
      setError(fieldName, {
        type: "server",
        message,
      });

      return unmappedMessages;
    }

    return message ? [...unmappedMessages, message] : unmappedMessages;
  }, []);
};

export const getCooperativeMemberFormValues = (data) => ({
  name: data?.name ?? "",
  functionId: data?.functionId ?? "",
  dateOfBirth: data?.dateOfBirth ? moment(data.dateOfBirth) : null,
  nif: data?.nif ?? "",
  bankAccountNumber: normalizeAOIBAN(data?.bankAccountNumber ?? ""),
  documentTypeId: data?.documentTypeId ?? "",
  identityDocumentNumber: data?.identityDocumentNumber ?? "",
  identityDocumentIssueDate: data?.identityDocumentIssueDate ? moment(data.identityDocumentIssueDate) : null,
  identityDocumentExpiryDate: data?.identityDocumentExpiryDate ? moment(data.identityDocumentExpiryDate) : null,
  identityDocument: [],
  entrepreneurshipCertificate: [],
  isActive: data?.isActive ?? true,
  inactivationReason: data?.inactivationReason ?? "",
  inactivationDate: data?.inactivationDate ? moment(data.inactivationDate) : null,
  inactivatedBy: data?.inactivatedBy ?? "",
});

export const resolveCooperativeMemberDocuments = (memberData) => {
  const availableDocuments = Array.isArray(memberData?.documents) ? memberData.documents : [];

  return {
    identityDocument: resolveDocument(
      availableDocuments,
      memberData?.identityDocumentName,
      memberData?.identityDocumentKey ?? memberData?.identityDocument?.documentKey ?? memberData?.identityDocument?.key,
      memberData?.documentTypeId,
    ),
    entrepreneurshipCertificateDocument: resolveDocument(
      availableDocuments,
      memberData?.entrepreneurshipCertificateDocumentName,
      memberData?.entrepreneurshipCertificateDocumentKey ??
        memberData?.entrepreneurshipCertificateDocument?.documentKey ??
        memberData?.entrepreneurshipCertificateDocument?.key,
      null,
    ),
    inactivationDocument: resolveDocument(
      availableDocuments,
      memberData?.inactivationDocumentName ?? memberData?.inactivationDocument?.documentName,
      memberData?.inactivationDocumentKey ??
        memberData?.inactivationDocument?.documentKey ??
        memberData?.inactivationDocument?.key,
      null,
      (document) => {
        const searchableLabel = [
          document?.documentTypeName,
          document?.documentTypeDescription,
          document?.documentName,
          document?.name,
        ]
          .filter(Boolean)
          .join(" ");

        return /inactiv/i.test(searchableLabel);
      },
    ),
  };
};

export const buildUpdateCooperativeMemberFormData = ({ values, memberId, memberData, documents }) => {
  const formData = new FormData();
  const identityDocumentFile = values.identityDocument?.[0];
  const entrepreneurshipCertificateFile = values.entrepreneurshipCertificate?.[0];
  const currentNif = values.nif ?? memberData?.nif;
  const currentIdentityDocumentNumber = values.identityDocumentNumber ?? memberData?.identityDocumentNumber;

  appendOptionalField(formData, "CooperativeMember.Id", memberId);
  appendOptionalField(formData, "CooperativeMember.CooperativeId", memberData?.cooperativeId);
  appendOptionalField(formData, "CooperativeMember.Name", values.name);
  appendOptionalField(formData, "CooperativeMember.FunctionId", values.functionId);
  appendOptionalField(formData, "CooperativeMember.DateOfBirth", formatOptionalDate(values.dateOfBirth));
  appendOptionalField(formData, "CooperativeMember.NIF", currentNif);
  appendOptionalField(formData, "CooperativeMember.DocumentTypeId", values.documentTypeId);
  appendOptionalField(formData, "CooperativeMember.IdentityDocumentNumber", currentIdentityDocumentNumber);
  appendOptionalField(
    formData,
    "CooperativeMember.IdentityDocumentIssueDate",
    formatOptionalDate(values.identityDocumentIssueDate),
  );
  appendOptionalField(
    formData,
    "CooperativeMember.IdentityDocumentExpiryDate",
    formatOptionalDate(values.identityDocumentExpiryDate),
  );
  appendOptionalField(
    formData,
    "CooperativeMember.BankAccountNumber",
    values.bankAccountNumber ? normalizeAOIBAN(values.bankAccountNumber) : null,
  );

  if (identityDocumentFile) {
    appendOptionalFile(formData, "IdentityDocument", identityDocumentFile);
  } else {
    appendExistingDocumentReference(
      formData,
      "IdentityDocument",
      documents.identityDocument?.documentKey,
      documents.identityDocument?.documentName,
    );
  }

  if (entrepreneurshipCertificateFile) {
    appendOptionalFile(formData, "EntrepreneurshipCertificateDocument", entrepreneurshipCertificateFile);
  } else {
    appendExistingDocumentReference(
      formData,
      "EntrepreneurshipCertificateDocument",
      documents.entrepreneurshipCertificateDocument?.documentKey,
      documents.entrepreneurshipCertificateDocument?.documentName,
    );
  }

  return formData;
};

export const buildInactivationCooperativeMemberFormData = (values) => {
  const formData = new FormData();

  appendOptionalField(formData, "InactivationReason", values.inactivationReasonInput);
  appendOptionalFile(formData, "InactivationDocument", values.inactivationDocument?.[0]);

  return formData;
};

export const invalidateCooperativeMemberQueries = (queryClient, memberId) => {
  queryClient.invalidateQueries({ queryKey: ["listCooperativeMembers"] });

  if (memberId) {
    queryClient.invalidateQueries({ queryKey: ["getCooperativeMemberDetails", memberId] });
  }
};

export const openCooperativeMemberConfirmationDialog = (dispatch, message, onConfirm) => {
  dispatch(
    showGenericTwoButtonDialogAction(
      message,
      "",
      async () => {
        await onConfirm();
      },
      () => {},
    ),
  );
};

export const showCooperativeMemberSuccessDialog = (dispatch, message, okAction) => {
  dispatch({
    type: DIALOG.SHOW,
    payload: {
      showGenericDialog: true,
      genericDialogData: {
        type: DIALOG.ONE_BUTTON_TYPE,
        isSuccess: true,
        buttonLabel: "Ok",
        message,
        okAction,
      },
    },
  });
};